import { connect, useSelector } from "react-redux";
import { Button, Drawer, Input, Menu, Space, Collapse, List, Divider, Tooltip } from "antd";
import { openOrCloseModalSnippets, updateActionFromAutomation } from "../../../../../hooks/automation";
import { ProCard } from "@ant-design/pro-components";
import { materialDark, materialLight } from "@uiw/codemirror-theme-material";
import { json } from "@codemirror/lang-json";
import CodeMirror, { basicSetup } from "@uiw/react-codemirror";
import { autocompletion } from "@codemirror/autocomplete";
import React, { useEffect, useRef, useState } from "react";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons"
import { BsFillPlayCircleFill } from "react-icons/bs";
import { getNameToSnippets } from "../Actions/utils/functions";
import SnippetSections from "./SnippetSection";
import { CODE_HELPER_PLACEHOLDER } from "./codeHelpers";
import { getRecordValueSuffix } from "./recordFieldPath";
import { toHandlebarsSegment } from "./handlebarsPath";
import { getAtFieldPath, parseFieldPath, setAtFieldPath } from "./fieldPath";
import _ from "lodash";

const Snippets = ({ actions, actionStep, trigger, snippets, formAction, onOpenOrCloseModalSnippets, onUpdateActionFromAutomation }) => {


    const { isDarkMode } = useSelector((state) => state.theme);
    const [code, setCode] = useState("");
    const editorRef = useRef();
    const { field, data, showModal } = snippets
    const { label, name, isCode = false, acceptEntryLoop = true, wholeField = false } = field;


    useEffect(() => {
        // O componente fica montado com o Drawer fechado, então o `code` precisa
        // ser recarregado do config a cada abertura — sem o `showModal` nas deps,
        // cancelar e reabrir o mesmo campo mostrava a edição descartada.
        if (!showModal) return;
        if (actions[actionStep] && actions[actionStep]['config'] && actions[actionStep]['config'][name]) {
            const dataInput = actions[actionStep]['config'][name];
            setCode(dataInput)
        } else {
            if (actions[actionStep] && name && name.includes(".")) {
                setCode(getCode(name))
            } else {
                setCode("")
            }

        }
    }, [actions, actionStep, name, showModal]);

    const closeDrawer = (e) => {
        onOpenOrCloseModalSnippets({ action: false })
    }

    // O editor abre com o conteúdo atual carregado — de propósito, para permitir
    // ajustar o que já está gravado. Só que a transação inseria o snippet na
    // posição do cursor, e um CodeMirror recém-criado (o Drawer é destroyOnHidden)
    // nasce com o cursor em 0: trocar um valor digitado por uma anotação
    // resultava em `{{ steps.1.success }}413431431`, com o valor antigo colado no
    // fim. A inserção também ignorava a seleção, então nem selecionar o texto
    // antes resolvia.
    //
    // Enquanto o usuário não tocou no editor, o snippet substitui o documento
    // inteiro — é o caso de "quero uma anotação no lugar do valor". Do primeiro
    // clique, tecla ou snippet em diante a faixa passa a ser a seleção real, e
    // compor ("Protocolo {{ x }}") continua possível.
    const touched = useRef(false);

    useEffect(() => {
        if (showModal) touched.current = false;
    }, [showModal]);

    const getInsertRange = (editor) => {
        if (!touched.current) {
            return { from: 0, to: editor.state.doc.length };
        }
        const { from, to } = editor.state.selection.main;
        return { from, to };
    };

    // Substituir o documento encurta o texto, então a posição calculada pelo
    // chamador pode cair fora dele — e o CodeMirror rejeita seleção fora de
    // faixa.
    const placeCursor = (editor, position) => {
        const cursorPos = Math.max(0, Math.min(position, editor.state.doc.length));
        editor.dispatch({
            selection: { anchor: cursorPos, head: cursorPos },
        });
        editor.focus();
    };

    // Depois de inserir a expressão o marcador fica selecionado: a próxima
    // anotação clicada entra no lugar dele (getInsertRange usa a seleção real a
    // partir do primeiro toque no editor), que é a ordem natural de montar
    // `moment(trigger["Data"]).format(...)` sem digitar nada.
    const selectRange = (editor, from, to) => {
        const max = editor.state.doc.length;
        editor.dispatch({
            selection: {
                anchor: Math.max(0, Math.min(from, max)),
                head: Math.max(0, Math.min(to, max)),
            },
        });
        editor.focus();
    };

    const insertCodeHelper = (helper) => {
        if (!editorRef.current) return;

        const editor = editorRef.current.view;
        const { from, to } = getInsertRange(editor);
        const text = helper.insert;

        editor.dispatch(editor.state.update({
            changes: { from, to, insert: text },
        }));
        touched.current = true;

        const placeholderAt = text.indexOf(CODE_HELPER_PLACEHOLDER);
        if (placeholderAt >= 0) {
            selectRange(editor, from + placeholderAt, from + placeholderAt + CODE_HELPER_PLACEHOLDER.length);
        } else {
            placeCursor(editor, from + text.length);
        }
    };

    const insertSnippetHelper = (snippet, value) => {
        if (editorRef.current) {
            let snippetsToInsert = `{{${value.requiresBlock ? "#" : ""}${snippet}${value.requiresBlock ? " " : ""}}}`;
            let lengthToPosition = 0
            if (value.requiresBlock) {
                lengthToPosition = snippetsToInsert.length - 2;
            } else {
                lengthToPosition = snippetsToInsert.length + 2;
            }


            if (value.requiresBlock) {
                snippetsToInsert = `${snippetsToInsert}{{/${snippet}}}`;
            }

            const editor = editorRef.current.view;
            const { from, to } = getInsertRange(editor);
            const transaction = editor.state.update({
                changes: { from, to, insert: snippetsToInsert },
            });
            editor.dispatch(transaction);
            touched.current = true;

            placeCursor(editor, from + lengthToPosition);
        }

    }

    const insertSnippets = (snippet, action = null, step, propFromLoop = false, record = null) => {
        if (editorRef.current) {
            let snippetFinal = getNameToSnippets(step, action, isCode)
            const editor = editorRef.current.view;
            const { from, to } = getInsertRange(editor);
            // Campo de formulário é lista de `{_id, value}`: a anotação sozinha
            // renderiza `[object Object]`, então entra o caminho até o valor.
            // Exceção é o campo marcado como `wholeField` — o desvio e o laço
            // querem a lista inteira, não um item dela.
            const valueSuffix = wholeField ? "" : getRecordValueSuffix(record, isCode);
            let functionNameWithParentheses = "";
            if (propFromLoop) {
                functionNameWithParentheses = isCode ? snippet : `{{ ${snippet} }}`;
            } else {
                // O segmento literal `[campo]` só entra quando o nome tem
                // caractere que o lexer do Handlebars recusa — acento e dígito
                // vão direto no caminho (ver handlebarsPath.js). No modo código
                // o nome vira string JS: JSON.stringify escapa aspas e
                // apóstrofos, que quebrariam a expressão.
                functionNameWithParentheses = isCode
                    ? `${snippetFinal}[${JSON.stringify(snippet)}]${valueSuffix}`
                    : `{{ ${snippetFinal}.${toHandlebarsSegment(snippet)}${valueSuffix} }}`;
            }


            const transaction = editor.state.update({
                changes: { from, to, insert: functionNameWithParentheses },
            });
            editor.dispatch(transaction);
            touched.current = true;

            placeCursor(editor, from + functionNameWithParentheses.length);
        }
    }

    const getCode = (name) => {
        const action = actions[actionStep];
        return _.toString(getAtFieldPath(action.config, name)) || "";
    }

    function saveConfig(e) {
        const action = actions[actionStep];

        // setAtFieldPath clona somente os nós do caminho: o estado no Redux não é
        // alterado no lugar, a atualização vai inteira pelo dispatch.
        const data = name.includes('.')
            ? setAtFieldPath(action.config, name, code)
            : { ...action.config, [name]: code };

        onUpdateActionFromAutomation({ step: actionStep, data });

        // O formAction (formulário do passo) é a fonte dos próximos dispatches:
        // qualquer mudança nele reenvia o config inteiro para o Redux. Como este
        // save escreve só no Redux, o formulário precisa ser atualizado junto —
        // senão a próxima edição no passo (ex.: adicionar outro campo em
        // Criar/Atualizar Registro) sobrescreve o config com o valor antigo e a
        // anotação recém-salva se perde. setFields não dispara onValuesChange,
        // então não há dispatch duplicado.
        if (formAction) {
            const rootKey = parseFieldPath(name)[0];
            formAction.setFields([{ name: rootKey, value: data[rootKey] }]);
        }
        closeDrawer();
    }

    return (
        (<Drawer
            height={"min(736px, 90vh)"}
            placement={"bottom"}
            destroyOnHidden
            title={label}
            onClose={closeDrawer}
            open={showModal}
            // Corpo em coluna flexível para o cartão abaixo receber uma altura
            // definida: sem ela, cada coluna se dimensionava pelo próprio
            // conteúdo e as duas caixas terminavam em alturas diferentes.
            styles={{
                body: {
                    paddingBottom: 24,
                    display: "flex",
                    flexDirection: "column",
                },
            }}
            extra={
                <Space>
                    <Button onClick={closeDrawer}>Cancelar</Button>
                    <Button type="primary" onClick={saveConfig}>
                        Salvar
                    </Button>
                </Space>
            }
        >
            <ProCard split={'vertical'}
                bordered
                style={{ flex: 1, minHeight: 0 }}
            >
                <ProCard colSpan={10} title={"Adicione vinculações ou use o menu à direita"} style={{ height: "100%" }}>
                    {/* Clicar ou digitar no editor significa "sei onde quero o
                        snippet": a partir daí ele é inserido no cursor em vez de
                        substituir o conteúdo. Em captura para pegar o evento
                        antes de o CodeMirror movê-lo. */}
                    <div
                        style={{ height: "100%", width: "100%" }}
                        onMouseDownCapture={() => { touched.current = true; }}
                        onKeyDownCapture={() => { touched.current = true; }}
                    >
                        <CodeMirror
                            // O editor ocupa a coluna inteira, para as duas
                            // caixas ficarem do mesmo tamanho. O `minHeight` é a
                            // rede de segurança: se a cadeia de alturas se
                            // quebrar, o editor cai numa altura útil em vez de
                            // colapsar para zero.
                            height="100%"
                            minHeight="calc(min(736px, 90vh) - 200px)"
                            width="100%"
                            ref={editorRef}
                            theme={isDarkMode ? materialDark : materialLight}
                            value={code}
                            extensions={[
                                json(),
                                basicSetup(),
                                autocompletion(),
                            ]}
                            onChange={(value) => {
                                setCode(value);
                            }}
                        />
                    </div>
                </ProCard>
                {/* A rolagem fica no corpo, não no cartão: a coluna tem a mesma
                    altura da do editor e a lista rola por baixo do título, que
                    fica fixo. */}
                <ProCard
                    colSpan={14}
                    title={"Anotações"}
                    style={{ height: "100%" }}
                    styles={{ body: { overflowY: 'auto' } }}
                >
                    <SnippetSections insertSnippetHelper={insertSnippetHelper} insertSnippets={insertSnippets} insertCodeHelper={insertCodeHelper} isCode={isCode} acceptEntryLoop={acceptEntryLoop} />
                </ProCard>
            </ProCard>
        </Drawer>)
    );
}
const mapStateToProps = ({ automation }) => ({
    actions: automation.actions,
    actionStep: automation.actionStep,
    trigger: automation.trigger,
    snippets: automation.snippets
});

const mapDispatchToProps = (dispatch) => {
    return {
        onOpenOrCloseModalSnippets: ({ action, fieldName, fieldLabel }) => dispatch(openOrCloseModalSnippets({
            action,
            fieldName,
            fieldLabel
        })),
        onUpdateActionFromAutomation: ({ step, data }) => dispatch(updateActionFromAutomation({ step, data }))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Snippets);
